'use client';
import { useState } from 'react';
import Image from 'next/image';
import { useMagneticButton } from '@/app/utils/useMagneticButton';
import styles from './ProductReviews.module.css';

export default function ProductReviews() {
  const [showAll, setShowAll] = useState(false);
  const magneticProps = useMagneticButton(0.15, 1.02);

  const reviewStats = {
    average: 4.0,
    total: 35000,
    breakdown: [
      { stars: 5, count: 14000, percentage: 40 },
      { stars: 4, count: 6000, percentage: 17 },
      { stars: 3, count: 4000, percentage: 11 },
      { stars: 2, count: 8000, percentage: 23 },
      { stars: 1, count: 9000, percentage: 26 },
    ],
    categories: [
      { name: 'Effectiveness', rating: 4.0 },
      { name: 'Quality', rating: 4.0 },
      { name: 'Value', rating: 3.5 },
      { name: 'Taste', rating: 3.0 },
    ],
  };

  const reviews = [
    {
      id: 1,
      author: 'Souhir ben amara',
      avatar: '/avatar-placeholder.png',
      rating: 5,
      date: '2 weeks ago',
      text: 'Amazing results! Been using this supplement for 3 months and noticed significant improvements in energy levels and overall wellness. The quality is outstanding and worth every penny.',
      images: [
        'https://longevity20155.sirv.com/zinc/1.webp',
        'https://longevity20155.sirv.com/minci/1.webp',
      ],
    },
    {
      id: 2,
      author: 'dlen flounei',
      avatar: '/avatar-placeholder.png',
      rating: 4,
      date: '1 month ago',
      text: 'Great product with noticeable benefits. Easy to take and no side effects. Would recommend to anyone looking for quality supplements.',
      images: [],
    },
    {
      id: 3,
      author: 'ahawa',
      avatar: '/avatar-placeholder.png',
      rating: 5,
      date: '3 weeks ago',
      text: 'Excellent supplement! Premium quality ingredients and fast shipping. Customer service was also very helpful.',
      images: [
        'https://longevity20155.sirv.com/testo/1.webp',
      ],
    },
  ];

  const displayedReviews = showAll ? reviews : reviews.slice(0, 2);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={i < rating ? styles.starFilled : styles.starEmpty}>
        ★
      </span>
    ));
  };

  return (
    <div className={styles.reviewsSection}>
      <h2 className={styles.title}>Reviews</h2>

      <div className={styles.content}>
        {/* Left: Stats */}
        <div className={styles.statsCard}>
          <div className={styles.overallRating}>
            <div className={styles.ratingNumber}>{reviewStats.average}</div>
            <div className={styles.stars}>{renderStars(Math.floor(reviewStats.average))}</div>
            <div className={styles.totalReviews}>
              {(reviewStats.total / 1000).toFixed(0)}K ratings
            </div>
          </div>

          <div className={styles.breakdown}>
            {reviewStats.breakdown.map((item) => (
              <div key={item.stars} className={styles.breakdownRow}>
                <span className={styles.breakdownLabel}>{item.stars}.0</span>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
                <span className={styles.breakdownCount}>
                  {(item.count / 1000).toFixed(0)}K reviews
                </span>
              </div>
            ))}
          </div>

          <div className={styles.categories}>
            {reviewStats.categories.map((cat) => (
              <div key={cat.name} className={styles.categoryTag}>
                <span className={styles.categoryRating}>{cat.rating}</span>
                <span className={styles.categoryName}>{cat.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Reviews List */}
        <div className={styles.reviewsList}>
          {displayedReviews.map((review) => (
            <div key={review.id} className={styles.reviewCard}>
              <div className={styles.reviewHeader}>
                <div className={styles.authorInfo}>
                  <div className={styles.avatar}>
                    {review.author.charAt(0)}
                  </div>
                  <div>
                    <div className={styles.authorName}>{review.author}</div>
                    <div className={styles.reviewDate}>{review.date}</div>
                  </div>
                </div>
                <div className={styles.reviewRating}>
                  <span className={styles.ratingValue}>{review.rating}.0</span>
                  <div className={styles.reviewStars}>
                    {renderStars(review.rating)}
                  </div>
                </div>
              </div>

              <p className={styles.reviewText}>{review.text}</p>

              {review.images.length > 0 && (
                <div className={styles.reviewImages}>
                  {review.images.map((img, idx) => (
                    <div key={idx} className={styles.reviewImage}>
                      <Image 
                        src={img} 
                        alt={`Review ${idx + 1}`}
                        width={80}
                        height={80}
                        style={{ objectFit: 'contain' }}
                        unoptimized
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <button
            className={styles.showMoreBtn}
            onClick={() => setShowAll(!showAll)}
            {...magneticProps}
          >
            {showAll ? 'Show Less ↑' : 'Read all reviews ↓'}
          </button>
        </div>
      </div>
    </div>
  );
}
